import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuthContext } from "../../Hooks/useAuthContext";

import SubmittingDialog from "../../Components/Loading/SubmittingDialog";
import Form from "react-bootstrap/Form";

import { apiService } from "../../Axios/apiService"; 

import SoilSamplingEditor from "../../Components/Projects/SoilSamplingEditor";

export default function CreateProject() {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const params = useParams();

    const [formErrors, setFormErrors] = useState({ title: "", customerId: "", general: "" });
    const [form, setForm] = useState({ title: "", customerId: "0", livestock: [], soilSamples: [], slurryStores: [] });
    const [submitting, setSubmitting] = useState(false);
    const [farms, setFarms] = useState([]);
    const [loading, setLoading] = useState();

    

    const setField = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    

    // Fetch farms once on mount
    useEffect(() => {
        if (!user?.token) return;

        // Load farms
        apiService("get", `farms/`, null,
            (response) => {
                setFarms(response);
            },
            (error) => {
                setFormErrors((prev) => ({
                    ...prev,
                    general: "Failed to load farms",
                }));
            }
        )

        // Load the project thats to be edited
        apiService("get", `projects/${params.id}`, null,
            (response) => {
                setForm({
                    title: response.title,
                    year: response.year,
                    farmId: response.farm.id,
                    status: response.status,
                    notes: response.notes,
                    reasonBehindNMP: response.reasonBehindNMP,
                    requirementsToMeet: response.requirementsToMeet,
                    herbalLeysCloverReseeds: response.herbalLeysCloverReseeds,
                    grasslandInformation: response.grasslandInformation,
                    arableDetails: response.arableDetails,
                    organicManureDetails: response.organicManureDetails,
                    fertiliserDetails: response.fertiliserDetails,
                    soilSamples: response.soilSamples || [], // ? ensure it�s always an array
                    livestock: response.livestock || [], // ? ensure it�s always an array
                    tasks: response.tasks || [],
                    slurryStores: response.slurryStores || [],
                });
                setLoading(false);
            },
            (error) => {
                setLoading(false);
            }
        )


        


    }, [user?.token]);

    const validateForm = () => {
        let errors = { title: "", farmId: "", general: "" };

        if (!form.title.trim()) {
            errors.title = "Project title is required";
        }
        if (!form.farmId || form.farmId === "0") {
            errors.farmId = "Farm is required";
        }

        setFormErrors(errors);
        return Object.values(errors).every((e) => e === "");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);


            apiService("put", `projects/${params.id}`, form,
                (response) => {
                    navigate(`/Projects/View/${response.id}`);
                },
                (error) => {
                    setFormErrors((prev) => ({
                        ...prev,
                        general: "Failed to create project. Please try again.",
                    }));
                }
            )

        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                setFormErrors((prev) => ({
                    ...prev,
                    general: "Could not connect to the server. Try again later.",
                }));
            } else if (err.response?.status === 400) {
                // server validation errors
                setFormErrors((prev) => ({
                    ...prev,
                    ...err.response.data.errors,
                }));
            } else {
                setFormErrors((prev) => ({
                    ...prev,
                    general: "An unknown error occurred. " + (err.code || ""),
                }));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (index, field, value) => {
        console.log("changing " + field)

        const updatedItems = form.livestock.map((item, i) =>
            i === index ? {
                ...item,
                [field]: value,
            } : item
        );
        setField("livestock", updatedItems);
        console.log("updated items: ", updatedItems);
    };

    const handleAddRow = () => {
        console.log("add row")
        setField("livestock", [...form.livestock, { type: "", number: 0, deleted: false }]);
    }

    const handleTasksChange = (index, field, value) => {
        console.log("changing task: " + field)

        const updatedItems = form.tasks.map((item, i) =>
            i === index ? {
                ...item,
                [field]: value,
            } : item
        );
        setField("tasks", updatedItems);
        console.log("updated items: ", updatedItems);
    };

    const handleSoilSampleChange = (index, field, value) => {
        console.log("changing " + field)

        const updatedItems = form.soilSamples.map((item, i) =>
            i === index ? {
                ...item,
                [field]: value,
            } : item
        );
        setField("soilSamples", updatedItems);
        console.log("updated soil sample: ", updatedItems);
    };

    const handleAddSoilSample = () => {
        console.log("add soil sample")
        setField("soilSamples", [...form.soilSamples, { sampleType: "GPS", status: "Not Performed" }]);
    }

    const handleSlurryStoreInputChange = (index, field, value) => {
        console.log("changing " + field);

        const updatedItems = form.slurryStores.map((item, i) => {
            if (i === index) {
                // Update the field first
                const updatedItem = { ...item, [field]: value };

                // Only calculate capacity if length, width, depth exist 
                const { length, width, depth, capacity } = updatedItem;
                if ((field === "length" || field === "width" || field === "depth")
                    && length && width && depth) {
                    updatedItem.capacity = Math.round((length * width * depth) * 10)/10;
                    console.log("calculated capacity: ", updatedItem.capacity);
                }

                return updatedItem;
            }
            return item;
        });

        setField("slurryStores", updatedItems);
        console.log("updated items: ", updatedItems);
    };

    const handleAddRowSlurryStores = () => {
        console.log("add row")
        setField("slurryStores", [...form.slurryStores, { typeOfStore: "", isMetres: true, isCovered: false, deleted: false }]);
    }

    return (
        <div className="page">
            {submitting && <SubmittingDialog />}

            <div className="row">
                <div className="col-12">
                    <h1>Edit Project</h1>
                </div>
            </div>

            <Form onSubmit={handleSubmit} noValidate>
                <div className="row">
                    {/* Project Details */}
                    <div className="col-md-6">
                        <h3>Project Details</h3>

                        <div className="row">
                            <div className="col-8 ps-0">
                                {/* Title */}
                                <div className="form-group">
                                    <label>Title</label>
                                    <Form.Control
                                        required
                                        type="text"
                                        placeholder="e.g Croft End Farm Soil Sample"
                                        isInvalid={!!formErrors.title}
                                        onChange={(e) => setField("title", e.target.value)}
                                        value={form.title}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.title}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                            <div className="col-4 select pe-0">
                                <label>Year</label>
                                <Form.Select
                                    onChange={(e) => setField("year", e.target.value)}
                                    isInvalid={!!formErrors.year}
                                    value={form.year}
                                    required
                                >
                                    <option value="2024">2024</option>
                                    <option value="2025">2025</option>
                                    <option value="2026">2026</option>
                                    <option value="2027">2027</option>
                                    <option value="2028">2028</option>
                                    <option value="2029">2029</option>
                                    <option value="2030">2030</option>

                                </Form.Select>
                            </div>
                        </div>

                        {/* Farm */}
                        <div className="form-group select">
                            <label>Farm</label>
                            <Form.Select
                                onChange={(e) => setField("farmId", e.target.value)}
                                isInvalid={!!formErrors.farmId}
                                value={form && form.farmId}
                                required
                            >
                                <option value="0">
                                    Please Select...
                                </option>
                                {farms.map((farm) => (
                                    <option key={farm.id} value={farm.id}>
                                        {farm.name}
                                        {farm.businessName && ` (${farm.businessName})`}
                                        {farm.primaryContact && " - "} {farm.primaryContact}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback className="text-danger" type="invalid">
                                {formErrors.farmId}
                            </Form.Control.Feedback>
                        </div>

                        {/* Status */}
                        <div className="form-group select">
                            <label>Status</label>
                            <Form.Select
                                onChange={(e) => setField("status", e.target.value)}
                                isInvalid={!!formErrors.status}
                                value={form.status}
                                required
                            >
                                <option>Created</option>
                                <option>To Start</option>
                                <option>On Hold</option>
                                <option>In Progress</option>
                                <option>To Invoice</option>
                                <option>Completed</option>
                                <option>Cancelled</option>

                            </Form.Select>
                            <Form.Control.Feedback className="text-danger" type="invalid">
                                {formErrors.status}
                            </Form.Control.Feedback>
                        </div>

                        {/* Notes */}
                        <div className="form-group">
                            <label>Notes</label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                onChange={(e) => setField("notes", e.target.value)}
                                isInvalid={!!formErrors.notes}
                                value={form.notes}
                            />
                            <Form.Control.Feedback className="text-danger" type="invalid">
                                {formErrors.notes}
                            </Form.Control.Feedback>
                        </div>
                    </div>

                    
                </div>

                <hr/>

                <div className="row">
                    <div className="col-12">
                        <h3>Tasks</h3>
                    </div>
                </div>


                <div className="col-md-12">
                    <div className="row">
                        <div className="col-3"><strong>Title</strong></div>


                        <div className="col-2"><strong>Status</strong></div>

                        
                        <div className="col-2">
                            <strong>Date Due</strong>
                        </div>

                        <div className="col-2">
                            <strong>Date Completed</strong>
                        </div>

                        <div className="col-2">
                            <strong>Date Invoiced</strong>
                        </div>

                    </div>


                    {form.tasks && form.tasks.map((item, index) => ( 

                        <div className="row">
                            <div className="col-3">{item.title}</div>
                            

                            <div className="col-2 select">
                                <Form.Select
                                    onChange={e => handleTasksChange(index, 'status', e.target.value)}
                                    value={item.status}
                                >
                                    <option value="Not Required">Not Required</option>
                                    <option value="Required">Required</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Cancelled">Cancelled</option>
                                    <option value="Awaiting Results">Awaiting Results</option>
                                    <option value="Invoiced">Invoiced</option>
                                    <option value="Completed">Completed</option>
                                    
                                </Form.Select>
                            </div>

                            {item.status != "Not Required" && (
                                <>
                                    <div className="col-2">
                                        <Form.Control
                                            required
                                            type="date"
                                            onChange={(e) => {
                                                const value = e.target.value === "" ? null : e.target.value;
                                                handleTasksChange(index, "dueDate", value);
                                            }}
                                            value={item.dueDate}
                                        />
                                    </div>

                                    <div className="col-2">
                                        <Form.Control
                                            required
                                            type="date"
                                            onChange={(e) => {
                                                const value = e.target.value === "" ? null : e.target.value;
                                                handleTasksChange(index, "completedDate", value);
                                            }}
                                            value={item.completedDate}
                                        />
                                    </div>

                                    {item.title != "Initial Farm Visit" && (
                                    <div className="col-2">
                                        <Form.Control
                                            required
                                            type="date"
                                            onChange={(e) => {
                                                const value = e.target.value === "" ? null : e.target.value;
                                                handleTasksChange(index, "invoicedDate", value);
                                            }}
                                            value={item.invoicedDate}
                                        />
                                        </div>
                                    )}
                                </>
                            )}

                            

                            

                        </div>
                    
                    ))}




                    

                    
                </div>

                <hr />

                {/* Soil sampling removed for the time being. Will be re-introduced after mapping 
                <SoilSamplingEditor soilSamples={form.soilSamples} setField={setField} /> */}

                




                <div className="row">
                    <div className="col-12">
                        { /* Farm Visit Sheet Survey */}
                        <div className="row">
                            <div className="col-md-12">
                                <h3 style={{ marginTop: 20 }}>Farm Visit Sheet</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        { /* Reason Behind NMP */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Reason for NMP, Red Tractor, SFI or EA. Does it require slurry storage, NVZ calculations and a SMP?</label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.reasonBehindNMP}
                                        onChange={(e) => setField("reasonBehindNMP", e.target.value)}
                                        value = { form.reasonBehindNMP ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.reasonBehindNMP}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>

                        { /* Herbal Leys/Clover/Reseeds */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Herbal Leys / Clover / Reseeds (% Mix)</label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.herbalLeysCloverReseeds}
                                        onChange={(e) => setField("herbalLeysCloverReseeds", e.target.value)}
                                        value={form.herbalLeysCloverReseeds ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.herbalLeysCloverReseeds}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>

                        { /* Grassland Information */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>
                                        Grassland: Is there grazing (cows are put out / sheep on the fields). <br />
                                        What grazing system is adopted?<br />
                                        Stocking rate?<br />
                                        How many silage / Hay cuts are there and when will they be cut?<br />
                                        Is there grazing afterwards<br />
                                        
                                    </label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.grasslandInformation}
                                        onChange={(e) => setField("grasslandInformation", e.target.value)}
                                        value={form.grasslandInformation ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.grasslandInformation}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="col-md-6">
                        { /* Arable */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Arable: Winter, spring barley, Winter wheat? When is it drilled & harvested? Approximate yield and is its whole crop, if not will straw be baled?<br/>
                                        When is Maize drilled? is under plastic and when is it harvested?<br />
                                        What is the approx yield?
                                    </label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.arableDetails}
                                        onChange={(e) => setField("arableDetails", e.target.value)}
                                        value={form.arableDetails ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.arableDetails}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>

                        { /* Slurry */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Organic Manure: List rates and application for each crop: grazing, silage, hay, maize or arable. Is organic manure applied dribble bar, injection (depth) or splash plate? When is it applied and what rate? FYM applied and what rate?</label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.organicManureDetails}
                                        onChange={(e) => setField("organicManureDetails", e.target.value)}
                                        value={form.organicManureDetails ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.organicManureDetails}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>

                        { /* Fertiliser Application */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Fertiliser: List for each cropping, Dates, rate and type of fertiliser. Lime </label>
                                    <Form.Control as="textarea" rows="5"
                                        placeholder=""
                                        isInvalid={formErrors && formErrors.fertiliserDetails}
                                        onChange={(e) => setField("fertiliserDetails", e.target.value)}
                                        value={form.fertiliserDetails ?? ""}
                                    />
                                    <Form.Control.Feedback className="text-danger" type="invalid">
                                        {formErrors.fertiliserDetails}
                                    </Form.Control.Feedback>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">

                            {  /* Livestock Section Header */}
                            <div className="row">
                                <div className="col-md-12">
                                    <h3 style={{ marginTop: 20 }}>Livestock</h3>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12">

                                    {  /* Livestock Table */}

                                    {form.livestock && form.livestock.length > 0 ? ( 
                                    <table width="100%">
                                        <thead>
                                            <tr>
                                                <th>Number</th>
                                                <th>Type</th>
                                                <th>Months Housed</th>
                                                <th>Slurry / Straw System</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.livestock && form.livestock.map((item, index) => (
                                                <tr style={{ display: item.deleted ? "none" : "" }} key={index}>
                                                    {/* Number */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.number}
                                                            onChange={e => handleInputChange(index, 'number', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Type */}
                                                    <td>
                                                        <div className="select">
                                                            <Form.Select
                                                                onChange={e => handleInputChange(index, 'type', e.target.value)}
                                                                value={item.type}
                                                            >
                                                                <option value="" >Please Select...</option>
                                                                <option value="Calf (all categories) up to 3 months">Calf (all categories) up to 3 months</option>
                                                                <option value="Dairy Heifer from 3 months and less than 13 months">Dairy Heifer from 3 months and less than 13 months</option>
                                                                <option value="Dairy Heifer from 13 months up to first calf">Dairy Heifer from 13 months up to first calf</option>
                                                                <option value="Dairy Cow after first calf (over 9000 litres milk yield)">Dairy Cow after first calf (over 9000 litres milk yield)</option>
                                                                <option value="Dairy Cow after first calf (6000 to 9000 litres milk yield)">Dairy Cow after first calf (6000 to 9000 litres milk yield)</option>
                                                                <option value="Dairy Cow after first calf (up to 6000 litres milk yield)">Dairy Cow after first calf (up to 6000 litres milk yield)</option>
                                                                <option value="Beef Heifer or Steer (castrated male) from 3 months and less than 13 months)">Beef Heifer or Steer (castrated male) from 3 months and less than 13 months)</option>
                                                                <option value="Beef Heifer or Steer from 13 months and less than 25 months">Beef Heifer or Steer from 13 months and less than 25 months</option>
                                                                <option value="Female or Steer for slaughter 25 months and over">Female or Steer for slaughter 25 months and over</option>
                                                                <option value="Female for breeding 25 months and over weighing up to 500kg">Female for breeding 25 months and over weighing up to 500kg</option>
                                                                <option value="Female for Breeding 25 months and over weighing over 500kg">Female for Breeding 25 months and over weighing over 500kg</option>
                                                                <option value="Non-Breeding Bull 3 months and over">Non-Breeding Bull 3 months and over</option>
                                                                <option value="Bull for breeding from 3 to 25 months">Bull for breeding from 3 to 25 months</option>
                                                                <option value="Bull for breeding 25 months and over">Bull for breeding 25 months and over</option>
                                                                <option value="Weaner Place, 7 to 13kg">Weaner Place, 7 to 13kg</option>
                                                                <option value="Weaner Place, 13 to 31kg">Weaner Place, 13 to 31kg</option>
                                                                <option value="Grower Place, 31 to 66kg (dry fed)">Grower Place, 31 to 66kg (dry fed)</option>
                                                                <option value="Grower Place, 31 to 66kg (liquid fed)">Grower Place, 31 to 66kg (liquid fed)</option>
                                                                <option value="Finisher Place, 66kg and over (dry fed)">Finisher Place, 66kg and over (dry fed)</option>
                                                                <option value="Finisher Place, 66kg and over (liquid fed)">Finisher Place, 66kg and over (liquid fed)</option>
                                                                <option value="Maiden Gilt Place, 66kg and over">Maiden Gilt Place, 66kg and over</option>
                                                                <option value="Sow Place, 66kg and over, with litter, up to 7kg, fed on diet supplement with synthetic amino acids">Sow Place, 66kg and over, with litter, up to 7kg, fed on diet supplement with synthetic amino acids</option>
                                                                <option value="Sow Place, 66kg and over, with litter, up to 7kg, fed on diet without synthetic amino acids">Sow Place, 66kg and over, with litter, up to 7kg, fed on diet without synthetic amino acids</option>

                                                            </Form.Select>
                                                        </div>
                                                    </td>

                                                    {/* Months Housed */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.monthsHoused}
                                                            onChange={e => handleInputChange(index, 'monthsHoused', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Slurry / Straw System */}
                                                    <td>
                                                        <div className="select">
                                                            <Form.Select
                                                                onChange={e => handleInputChange(index, 'slurryOrStraw', e.target.value)}
                                                                value={item.slurryOrStraw}
                                                            >
                                                                <option value="Slurry">Slurry</option>
                                                                <option value="Straw">Straw</option>
                                                            

                                                            </Form.Select>
                                                        </div>
                                                    </td>

                                                    <td><button type="button" className="btn btn-other" onClick={() => { handleInputChange(index, 'deleted', true) }}>X</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        </table>) : (
                                                                                <div>No livestock items added yet.</div>
                                    )}
                                    

                                    {formErrors.livestock && (<div className="alert alert-warning">{formErrors.livestock}</div>)}
                                    <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={handleAddRow}>Add Item</button>


                                </div>
                            </div>


                            

                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">

                            {  /* Slurry Store Section Header */}
                            <div className="row">
                                <div className="col-md-12">
                                    <h3 style={{ marginTop: 20 }}>Slurry Store</h3>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12">

                                { /* Slurry Store Table */}
                                {form.slurryStores && form.slurryStores.length > 0 ? (
                                    <table width="100%">
                                        <thead>
                                            <tr>
                                                <th>Type Of Store</th>
                                                <th>Length</th>
                                                <th>Depth</th>
                                                <th>Width</th>
                                                <th>Capacity</th>
                                                <th>Covered?</th>
                                                <th>Drainage Area?</th>
                                                <th>Units</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.slurryStores && form.slurryStores.map((item, index) => (
                                                <tr style={{ display: item.deleted ? "none" : "" }} key={index}>
                                                    {/* Type Of Store */}
                                                    <td>
                                                        <div className="select">
                                                            <Form.Select
                                                                onChange={e => handleSlurryStoreInputChange(index, 'typeOfStore', e.target.value)}
                                                                value={item.typeOfStore}
                                                            >
                                                                <option value="">Please Select...</option>
                                                                <option value="Round Tower">Round Tower</option>
                                                                <option value="Concrete Square">Concrete Square</option>
                                                                <option value="Lagoon">Lagoon</option>
                                                                <option value="Slatts">Slatts</option>
                                                                <option value="Other">Other</option>
                                                            </Form.Select>
                                                        </div>
                                                    </td>

                                                    {/* Length */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.length}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'length', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Depth */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.depth}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'depth', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Width */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.width}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'width', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Capacity */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.capacity}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'capacity', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Covered? */}
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Yes"
                                                            onChange={e => handleSlurryStoreInputChange(index, 'isCovered', e.target.checked)}
                                                            checked={item.isCovered === true}
                                                        />
                                                    </td>
                                                    {/* Drainage Area */}
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.drainageArea}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'drainageArea', e.target.value)}
                                                        />
                                                    </td>
                                                    {/* Units */}
                                                    <td>
                                                        <Form.Check
                                                            type="radio"
                                                            label="Metres"
                                                            name={`units-${index}`}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'isMetres', true)}
                                                            checked={item.isMetres === true}
                                                        />
                                                        <Form.Check
                                                            type="radio"
                                                            label="Feet"
                                                            name={`units-${index}`}
                                                            onChange={e => handleSlurryStoreInputChange(index, 'isMetres', false)}
                                                            checked={item.isMetres === false}
                                                        />
                                                    </td>

                                                    <td><button type="button" className="btn btn-other" onClick={() => { handleSlurryStoreInputChange(index, 'deleted', true) }}>X</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div>
                                        <em>No slurry store information provided</em><br />
                                    </div>
                                )}



                                {formErrors.slurryStores && (<div className="alert alert-warning">{formErrors.slurryStores}</div>)}
                                <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={handleAddRowSlurryStores}>Add Item</button>

                                </div>
                                </div>

                        </div>
                    </div>
                </div>

                <hr />

                {formErrors.general && (
                    <div className="alert alert-danger">{formErrors.general}</div>
                )}

                <div className="row">
                    <div className="col-md-12">
                        <button className="btn btn-primary" type="submit">
                            Update Project
                        </button>
                        <button
                            className="btn btn-other"
                            style={{ marginLeft: 10 }}
                            onClick={() => navigate(`/Projects/`)}
                            type="button"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}
